import { env } from "../../Consts/config";
import {
  fetchAndActivate,
  getBoolean,
  getRemoteConfig,
} from "firebase/remote-config";
import {
  DASHBOARD_FEATURE_REMOTE_CONFIG_KEYS,
  DEFAULT_DASHBOARD_FEATURE_FLAGS,
  APP_FEATURE_REMOTE_CONFIG_KEYS,
  getDefaultAppFeatureAccess,
  type AppFeatureKey,
  type DashboardFeatureFlagKey,
} from "../../Consts/features";

type AppFeatureAccessConfig = Record<AppFeatureKey, boolean>;
type DashboardFeatureFlagsConfig = Record<DashboardFeatureFlagKey, boolean>;

type FeatureConfigListener = () => void;

const defaultAppFeatureAccess = getDefaultAppFeatureAccess(env.ENV);

let appFeatureAccessConfig: AppFeatureAccessConfig = {
  ...defaultAppFeatureAccess,
};
let dashboardFeatureFlagsConfig: DashboardFeatureFlagsConfig = {
  ...DEFAULT_DASHBOARD_FEATURE_FLAGS,
};

const featureConfigListeners = new Set<FeatureConfigListener>();

const notifyFeatureConfigListeners = () => {
  featureConfigListeners.forEach((listener) => listener());
};

const readAppFeatureAccessFromRemoteConfig = (): AppFeatureAccessConfig => {
  const remoteConfig = getRemoteConfig();
  const nextConfig = { ...defaultAppFeatureAccess };

  (Object.keys(APP_FEATURE_REMOTE_CONFIG_KEYS) as AppFeatureKey[]).forEach(
    (featureKey) => {
      const remoteConfigKey = APP_FEATURE_REMOTE_CONFIG_KEYS[featureKey];
      nextConfig[featureKey] = getBoolean(remoteConfig, remoteConfigKey);
    }
  );

  return nextConfig;
};

const readDashboardFeatureFlagsFromRemoteConfig =
  (): DashboardFeatureFlagsConfig => {
    const remoteConfig = getRemoteConfig();
    const nextConfig = { ...DEFAULT_DASHBOARD_FEATURE_FLAGS };

    (Object.keys(DASHBOARD_FEATURE_REMOTE_CONFIG_KEYS) as DashboardFeatureFlagKey[]).forEach(
      (featureKey) => {
        const remoteConfigKey = DASHBOARD_FEATURE_REMOTE_CONFIG_KEYS[featureKey];
        nextConfig[featureKey] = getBoolean(remoteConfig, remoteConfigKey);
      }
    );

    return nextConfig;
  };

const refreshAppFeatureAccessConfig = () => {
  try {
    appFeatureAccessConfig = readAppFeatureAccessFromRemoteConfig();
    dashboardFeatureFlagsConfig = readDashboardFeatureFlagsFromRemoteConfig();
  } catch (error) {
    console.error("Error reading app feature access from remote config", error);
  } finally {
    notifyFeatureConfigListeners();
  }
};

const getAppFeatureAccessConfig = () => appFeatureAccessConfig;
const getDashboardFeatureFlagsConfig = () => dashboardFeatureFlagsConfig;

const isAppFeatureEnabled = (featureKey: AppFeatureKey) =>
  appFeatureAccessConfig[featureKey];
const isDashboardFeatureFlagEnabled = (featureKey: DashboardFeatureFlagKey) =>
  dashboardFeatureFlagsConfig[featureKey];

const subscribeAppFeatureAccessConfig = (listener: FeatureConfigListener) => {
  featureConfigListeners.add(listener);
  return () => {
    featureConfigListeners.delete(listener);
  };
};

const fetchRemoteConfig = async () => {
  try {
    const remoteConfig = getRemoteConfig();
    remoteConfig.defaultConfig = {
      feat_resources_enabled: true,
      ...Object.fromEntries(
        Object.entries(defaultAppFeatureAccess).map(([featureKey, enabled]) => [
          APP_FEATURE_REMOTE_CONFIG_KEYS[featureKey as AppFeatureKey],
          enabled,
        ])
      ),
      ...Object.fromEntries(
        Object.entries(DEFAULT_DASHBOARD_FEATURE_FLAGS).map(([featureKey, enabled]) => [
          DASHBOARD_FEATURE_REMOTE_CONFIG_KEYS[
            featureKey as DashboardFeatureFlagKey
          ],
          enabled,
        ])
      ),
    };
    if (env.IS_LOCAL || env.IS_DEBUG) {
      remoteConfig.settings = {
        minimumFetchIntervalMillis: 0,
        fetchTimeoutMillis: 0,
      };
    }
    await fetchAndActivate(remoteConfig);
  } catch (error) {
    console.error("Error fetching remote config", error);
  } finally {
    refreshAppFeatureAccessConfig();
  }
};

const featResourcesEnabled = () => {
  const remoteConfig = getRemoteConfig();
  return getBoolean(remoteConfig, "feat_resources_enabled");
};

export { featResourcesEnabled, fetchRemoteConfig };
export {
  getAppFeatureAccessConfig,
  getDashboardFeatureFlagsConfig,
  isAppFeatureEnabled,
  isDashboardFeatureFlagEnabled,
  subscribeAppFeatureAccessConfig,
};
export type { AppFeatureAccessConfig, DashboardFeatureFlagsConfig };
